import type {
    Conversation,
    InteractionID,
    ReadonlyActionStatusMap,
} from "@khan/guide-types";

import parseFormattedText from "../utils/parse-formatted-text.ts";

/**
 * Append an answer to the existing conversation.
 *
 * This is used to update the conversation as a response is being streamed.
 *
 * @param input.answer The answer to append to the conversation.
 * @param input.conversation The current conversation to be appended or updated.
 * @param input.interactionId The interactionId of the incoming message.
 * @param input.isComplete Whether the answer is finished streaming or not.
 * @param input.loadingMessage A loading message to show below the streamed answer (optional).
 *
 * @returns The updated conversation if there was something to append, or the
 * original conversation if there was nothing to append.
 */
export const updateConversationWithStreamedAnswer = ({
    answer,
    conversation,
    interactionId,
    actionStatusMap,
}: {
    answer: string;
    conversation: Conversation;
    interactionId: InteractionID;
    actionStatusMap: ReadonlyActionStatusMap;
}): Conversation => {
    // We do not want to render any incomplete delimited expressions so we'll
    // defer updating the conversation until we no longer encounter text chunks
    // containing delimiters.
    const parsedText = parseFormattedText(answer.trim());
    if (
        parsedText.some(
            // @ts-expect-error - TS2339 - Property 'hasStartDelimiter' does not exist on type 'FormattedMessageChunk'.
            ({type, hasStartDelimiter}) => type === "text" && hasStartDelimiter,
        )
    ) {
        return conversation;
    }

    // We want to replace the most recent incoming
    // message, if we have one, so that we stream
    // the response, rather than append it.
    const lastMessage =
        conversation.length === 0
            ? null
            : conversation[conversation.length - 1];

    // If we're attempting to update the streaming answer for an incoming
    // message that is no longer generating a response, then we should not
    // update the conversation. This can happen if we have streaming handlers
    // trigger out of order (i.e. onAnswer fires after onMetadata).
    if (
        lastMessage?.type === "incoming" &&
        lastMessage.interactionId === interactionId &&
        !lastMessage?.isGeneratingResponse
    ) {
        return conversation;
    }

    // If the last message is marked as an interim message (
    // isGeneratingResponse = true), then it is a message that should be
    // updated with the latest answer.
    const conversationSoFar = lastMessage?.isGeneratingResponse
        ? conversation.slice(0, -1)
        : conversation;
    return [
        ...conversationSoFar,
        {
            type: "incoming",
            text: answer,
            user: "Teacher",
            uiOnly: true,
            serverOnly: false,
            interactionId,
            isGeneratingResponse: true, // This is an interim message.
            actionStatusMap,
        },
    ];
};
